import React, { useState, useEffect } from "react";
import SeoHead from "../components/SeoHead";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, ArrowLeft, Check, Sparkles, Star,
  Target, Search, Pen, Code2, Share2, Zap, X,
  ChevronRight, MessageCircle, Users
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useCurrency } from "../contexts/CurrencyContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Reveal from "../components/Reveal";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EASE = [0.22, 1, 0.36, 1];

// Service definitions with pricing AND key features
const SERVICES_DATA = [
  {
    id: "meta-ads", icon: Target, color: "from-brand-pink to-brand-magenta",
    pricing: { starter: 12500, pro: 24500, premium: 49500 },
    features: {
      starter: ["Ayda 8 post + 4 video kreatif", "1 Meta Ads hesabı", "Aylık rapor", "E-posta destek"],
      pro: ["Ayda 12 post + 6 video kreatif", "2 Meta Ads hesabı + 1 katalog", "Haftalık rapor & optimizasyon", "Pixel + Conversion API kurulumu", "WhatsApp öncelikli destek"],
      premium: ["Ayda 18 post + 10 video kreatif", "Sınırsız hesap & kampanya", "Günlük takip + haftalık call", "Advanced attribution kurulumu", "Özel Slack kanalı, mesai içi 7/24"],
    },
    features_en: {
      starter: ["8 posts + 4 video creatives/month", "1 Meta Ads account", "Monthly report", "Email support"],
      pro: ["12 posts + 6 video creatives/month", "2 Meta Ads accounts + 1 catalog", "Weekly report & optimization", "Pixel + Conversion API setup", "WhatsApp priority support"],
      premium: ["18 posts + 10 video creatives/month", "Unlimited accounts & campaigns", "Daily tracking + weekly call", "Advanced attribution setup", "Private Slack channel 24/7"],
    },
  },
  {
    id: "google-ads", icon: Search, color: "from-blue-500 to-cyan-500",
    pricing: { starter: 14500, pro: 27500, premium: 54500 },
    features: {
      starter: ["Search kampanyası kurulumu", "Anahtar kelime araştırması", "Aylık raporlama", "GA4 entegrasyonu"],
      pro: ["Search + Display + PMax", "Conversion takibi kurulumu", "Haftalık bid optimizasyonu", "A/B test & kalite puanı yönetimi"],
      premium: ["Search + PMax + YouTube", "Server-side conversion API", "Günlük optimizasyon", "Özel raporlama dashboard"],
    },
    features_en: {
      starter: ["Search campaign setup", "Keyword research", "Monthly reporting", "GA4 integration"],
      pro: ["Search + Display + PMax", "Conversion tracking setup", "Weekly bid optimization", "A/B test & quality score management"],
      premium: ["Search + PMax + YouTube", "Server-side conversion API", "Daily optimization", "Custom reporting dashboard"],
    },
  },
  {
    id: "seo", icon: Zap, color: "from-emerald-500 to-teal-500",
    pricing: { starter: 9500, pro: 19500, premium: 39500 },
    features: {
      starter: ["Teknik SEO audit", "10 anahtar kelime optimizasyonu", "Aylık içerik takvimi (2 yazı)", "On-page optimizasyon"],
      pro: ["Kapsamlı teknik audit + fix", "30 anahtar kelime", "Ayda 4 blog yazısı + backlink", "Schema markup + Core Web Vitals"],
      premium: ["Tam teknik SEO yönetimi", "Sınırsız anahtar kelime", "Haftalık içerik + link inşası", "Rakip analizi + özel raporlar"],
    },
    features_en: {
      starter: ["Technical SEO audit", "10 keyword optimization", "Monthly content calendar (2 posts)", "On-page optimization"],
      pro: ["Comprehensive technical audit + fix", "30 keywords", "4 blog posts/month + backlinks", "Schema markup + Core Web Vitals"],
      premium: ["Full technical SEO management", "Unlimited keywords", "Weekly content + link building", "Competitor analysis + custom reports"],
    },
  },
  {
    id: "creative", icon: Pen, color: "from-amber-500 to-orange-400",
    pricing: { starter: 6500, pro: 12500, premium: 24500 },
    features: {
      starter: ["Ayda 8 statik post tasarımı", "2 story/reel kapağı", "3 iş günü teslim", "1 revizyon hakkı"],
      pro: ["Ayda 16 statik + 4 carousel", "6 story/reel kapağı", "48 saat içinde teslim", "Sınırsız revizyon"],
      premium: ["Ayda 32 post + video editing", "Sınırsız story/reel kapağı", "24 saat içinde teslim", "Özel marka rehberi + şablon"],
    },
    features_en: {
      starter: ["8 static post designs/month", "2 story/reel covers", "3 business day delivery", "1 revision"],
      pro: ["16 static + 4 carousels/month", "6 story/reel covers", "Delivery within 48 hours", "Unlimited revisions"],
      premium: ["32 posts + video editing/month", "Unlimited story/reel covers", "Delivery within 24 hours", "Custom brand guide + templates"],
    },
  },
  {
    id: "social-media", icon: Share2, color: "from-purple-500 to-pink-500",
    pricing: { starter: 7500, pro: 14500, premium: 29500 },
    features: {
      starter: ["Ayda 12 post yayını", "2 platform yönetimi", "Yorum moderasyonu", "Aylık analiz raporu"],
      pro: ["Ayda 20 post + story", "4 platform yönetimi", "Aktif topluluk yönetimi", "Haftalık performans raporu"],
      premium: ["Ayda 30+ içerik üretim + yayın", "Tüm platformlar", "7/24 topluluk yönetimi", "Influencer koordinasyonu"],
    },
    features_en: {
      starter: ["12 posts/month", "2 platform management", "Comment moderation", "Monthly analytics report"],
      pro: ["20 posts + stories/month", "4 platform management", "Active community management", "Weekly performance report"],
      premium: ["30+ content production + publishing/month", "All platforms", "24/7 community management", "Influencer coordination"],
    },
  },
  {
    id: "web-development", icon: Code2, color: "from-indigo-600 to-blue-500",
    pricing: { starter: 19500, pro: 49500, premium: 99500 },
    features: {
      starter: ["5 sayfalık kurumsal site", "Mobil uyumlu tasarım", "Temel SEO kurulumu", "1 yıl teknik destek"],
      pro: ["15 sayfaya kadar web sitesi", "E-ticaret entegrasyonu", "Gelişmiş SEO + hız optimizasyonu", "CMS paneli"],
      premium: ["Sınırsız sayfa + özel geliştirme", "API entegrasyonları", "Performans + güvenlik paketi", "Öncelikli destek + bakım"],
    },
    features_en: {
      starter: ["5-page corporate website", "Mobile-responsive design", "Basic SEO setup", "1 year technical support"],
      pro: ["Up to 15-page website", "E-commerce integration", "Advanced SEO + speed optimization", "CMS panel"],
      premium: ["Unlimited pages + custom development", "API integrations", "Performance + security package", "Priority support + maintenance"],
    },
  },
];

const PRIORITIES = [
  { id: "new_customers", icon: Users },
  { id: "awareness",     icon: Star },
  { id: "sales",         icon: Target },
  { id: "content",       icon: Pen },
];

const TIER_ORDER = ["starter", "pro", "premium"];

// i18n helpers
const LABELS = {
  tr: {
    page_title: "Paketini Bul",
    eyebrow: "AKILLI EŞLEŞTİRME",
    hero_title: "Size uygun paketi",
    hero_accent: "3 adımda bulun",
    hero_sub: "Hangi hizmetleri istediğinizi seçin, önceliğinizi belirtin, size özel fiyat teklifini anında görün.",
    step1_title: "Hangi hizmetleri istiyorsunuz?",
    step1_sub: "Birden fazla seçebilirsiniz",
    step2_title: "Önceliğiniz nedir?",
    step2_sub: "Birden fazla seçebilirsiniz",
    step3_title: "Size özel paket teklifiniz",
    step3_sub: "Seçtiğiniz hizmetler için en uygun paketler",
    back: "Geri",
    next: "Devam",
    show: "Teklifimi Göster",
    total: "Toplam Aylık",
    contact_cta: "Bu Paketi İstiyorum — WhatsApp",
    tier: { starter: "Başlangıç", pro: "Profesyonel", popular: "Profesyonel", premium: "Premium" },
    service: {
      "meta-ads": "Meta Ads",
      "google-ads": "Google Ads",
      "seo": "SEO & İçerik",
      "creative": "Kreatif Tasarım",
      "social-media": "Sosyal Medya",
      "web-development": "Web Geliştirme",
    },
    priority: {
      new_customers: "Yeni Müşteri Kazanımı",
      awareness: "Marka Bilinirliği",
      sales: "Satış Artışı",
      content: "İçerik Üretimi",
    },
    select_at_least: "En az 1 hizmet seçin",
    select_priority: "En az 1 öncelik seçin",
    completed: "Tamamlanan Projeler",
    reviews_title: "Müşterilerimiz Ne Diyor?",
    month: "/ay",
    project: "/proje",
    recommended: "Önerilen",
    wa_msg: (services, tiers, total) =>
      `Merhaba Yastık Medya Ekibi,\n\nPaketini Bul sayfasından size ulaşıyorum.\n\n${services.map((s, i) => `📌 ${s}: ${tiers[i]} Paketi`).join('\n')}\n\n💰 Toplam: ${total}\n\nDetayları görüşmek isterim.`,
  },
  en: {
    page_title: "Find Your Plan",
    eyebrow: "SMART MATCHING",
    hero_title: "Find the right plan",
    hero_accent: "in 3 steps",
    hero_sub: "Select the services you want, set your priorities, and instantly see your personalized quote.",
    step1_title: "Which services do you need?",
    step1_sub: "You can select multiple",
    step2_title: "What are your priorities?",
    step2_sub: "You can select multiple",
    step3_title: "Your personalized package",
    step3_sub: "Best packages for your selected services",
    back: "Back",
    next: "Continue",
    show: "Show My Quote",
    total: "Monthly Total",
    contact_cta: "I Want This Package — WhatsApp",
    tier: { starter: "Starter", pro: "Professional", premium: "Premium" },
    service: {
      "meta-ads": "Meta Ads",
      "google-ads": "Google Ads",
      "seo": "SEO & Content",
      "creative": "Creative Design",
      "social-media": "Social Media",
      "web-development": "Web Development",
    },
    priority: {
      new_customers: "New Customer Acquisition",
      awareness: "Brand Awareness",
      sales: "Sales Growth",
      content: "Content Production",
    },
    select_at_least: "Select at least 1 service",
    select_priority: "Select at least 1 priority",
    completed: "Completed Projects",
    reviews_title: "What Our Clients Say",
    month: "/month",
    project: "/project",
    recommended: "Recommended",
    wa_msg: (services, tiers, total) =>
      `Hello Yastık Medya Team,\n\nI'm reaching you from the Package Finder.\n\n${services.map((s, i) => `📌 ${s}: ${tiers[i]} Package`).join('\n')}\n\n💰 Total: ${total}\n\nI'd like to discuss the details.`,
  },
};

// Stats for social proof
const STATS = [
  { value: "200+", label: { tr: "Tamamlanan Proje", en: "Completed Projects" } },
  { value: "15+", label: { tr: "Yıl Deneyim", en: "Years Experience" } },
  { value: "120+", label: { tr: "Aktif Müşteri", en: "Active Clients" } },
  { value: "6", label: { tr: "Dilde Hizmet", en: "Languages" } },
];

function StepIndicator({ step, total }) {
  return (
    <div className="flex items-center gap-2 mb-8">
      {Array.from({ length: total }).map((_, i) => (
        <React.Fragment key={i}>
          <div className={`h-2 rounded-full transition-all duration-500 ${i < step ? "bg-brand-pink" : i === step - 1 ? "bg-brand-pink w-8" : "bg-mist"} ${i === step - 1 ? "w-8" : "w-2"}`} />
        </React.Fragment>
      ))}
      <span className="ml-2 text-xs text-ink/50">{step} / {total}</span>
    </div>
  );
}

export default function PackageFinderPage() {
  const { lang } = useLanguage();
  const { format: formatPrice } = useCurrency();
  const L = LABELS[lang] || LABELS.en;
  const lk = lang === "tr" ? "tr" : "en";

  const [step, setStep] = useState(1);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedPriorities, setSelectedPriorities] = useState([]);
  const [selectedTiers, setSelectedTiers] = useState({});
  const [reviews, setReviews] = useState([]);
  const [error, setError] = useState("");
  const [liveServicesData, setLiveServicesData] = useState({}); // slug → pricing from API

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/services`, { params: { lang } })
      .then((r) => {
        const map = {};
        (r.data || []).forEach((s) => {
          if (s.slug) map[s.slug] = s;
        });
        setLiveServicesData(map);
      })
      .catch(() => {});
  }, [lang]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/reviews`, { params: { lang } })
      .then((r) => setReviews(r.data || []))
      .catch(() => {});
  }, [lang]);

  // Auto-select recommended tier (pro) per service
  useEffect(() => {
    const newTiers = {};
    selectedServices.forEach((id) => {
      newTiers[id] = selectedTiers[id] || "pro";
    });
    setSelectedTiers(newTiers);
  }, [selectedServices]); // eslint-disable-line

  const toggleService = (id) => {
    setSelectedServices((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const togglePriority = (id) => {
    setSelectedPriorities((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const nextStep = () => {
    if (step === 1 && selectedServices.length === 0) { setError(L.select_at_least); return; }
    if (step === 2 && selectedPriorities.length === 0) { setError(L.select_priority); return; }
    setError("");
    setStep((s) => s + 1);
  };

  // Total price — use live API data if available, fallback to static
  const getPricing = (serviceId) => {
    const live = liveServicesData[serviceId];
    if (live?.pricing?.length) {
      const priceMap = {};
      live.pricing.forEach((t) => { priceMap[t.id] = t.price_try; });
      return priceMap;
    }
    return SERVICES_DATA.find((s) => s.id === serviceId)?.pricing || {};
  };

  const getTierFeatures = (serviceId, tier) => {
    const live = liveServicesData[serviceId];
    if (live?.pricing?.length) {
      const t = live.pricing.find((p) => p.id === tier);
      if (t) {
        // Try language-specific first, then generic
        if (t[`features_${lang}`]?.length) return t[`features_${lang}`];
        if (t.features?.length) return t.features;
      }
    }
    const svc = SERVICES_DATA.find((s) => s.id === serviceId);
    return lang === "tr" ? (svc?.features?.[tier] || []) : (svc?.features_en?.[tier] || svc?.features?.[tier] || []);
  };

  const totalPrice = selectedServices.reduce((sum, id) => {
    const pricing = getPricing(id);
    const tier = selectedTiers[id] || "pro";
    return sum + (pricing[tier] || 0);
  }, 0);

  const sendWhatsApp = () => {
    const serviceNames = selectedServices.map((id) => L.service[id] || id);
    const tierNames = selectedServices.map((id) => L.tier[selectedTiers[id] || "pro"] || "pro");
    const totalStr = formatPrice(totalPrice) + (lang === "tr" ? "/ay" : "/mo");
    const msg = L.wa_msg(serviceNames, tierNames, totalStr);
    window.open(`https://wa.me/908508884865?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const pageTitle = `${L.page_title} — Yastık Medya`;

  return (
    <div className="min-h-screen bg-cream text-ink" data-testid="package-finder-page">
      <SeoHead title={pageTitle} description={L.hero_sub} path={`/${lang}/paketini-bul`} imageKey="paketini-bul" lang={lang} />
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-cream pt-14 pb-10">
        <div className="pointer-events-none absolute -top-32 right-[-8%] h-[500px] w-[500px] rounded-full bg-brand-pink/15 blur-3xl" />
        <div className="container-page text-center relative">
          <motion.span
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="eyebrow justify-center"
          >
            <Sparkles className="h-3 w-3 text-brand-pink" />
            {L.eyebrow}
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="mt-5 font-display text-display-1 font-bold leading-[0.95]"
          >
            {L.hero_title}{" "}
            <span className="gold-foil-text">{L.hero_accent}</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-6 max-w-xl mx-auto text-body-lg text-ink/65"
          >
            {L.hero_sub}
          </motion.p>
        </div>
      </section>

      {/* Stats strip */}
      <div className="border-y border-mist bg-cream/80 py-4">
        <div className="container-page flex flex-wrap items-center justify-center gap-8">
          {STATS.map((s) => (
            <div key={s.value} className="text-center">
              <div className="font-display text-h1 font-black text-brand-pink leading-none">{s.value}</div>
              <div className="text-xs text-ink/55 mt-0.5">{s.label[lk] || s.label.en}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Main wizard */}
      <section className="container-page py-section">
        <div className="max-w-2xl mx-auto">
          <StepIndicator step={step} total={3} />

          <AnimatePresence mode="wait">
            {/* STEP 1: Services */}
            {step === 1 && (
              <motion.div key="step1"
                initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.35, ease: EASE }}>
                <h2 className="font-display text-display-2 font-bold text-ink mb-2">{L.step1_title}</h2>
                <p className="text-ink/55 mb-8">{L.step1_sub}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {SERVICES_DATA.map((svc) => {
                    const selected = selectedServices.includes(svc.id);
                    const Icon = svc.icon;
                    return (
                      <button
                        key={svc.id}
                        type="button"
                        onClick={() => toggleService(svc.id)}
                        className={`group relative rounded-2xl border-2 p-5 text-left transition-all duration-200
                          ${selected
                            ? "border-brand-pink bg-brand-pink/5 shadow-md"
                            : "border-mist bg-white hover:border-brand-pink/40"}`}
                      >
                        <div className={`inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${svc.color} text-white mb-3`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="font-display text-sm font-semibold text-ink leading-snug">
                          {L.service[svc.id]}
                        </div>
                        {selected && (
                          <span className="absolute top-2 right-2 h-5 w-5 rounded-full bg-brand-pink flex items-center justify-center">
                            <Check className="h-3 w-3 text-white" strokeWidth={3} />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* STEP 2: Priorities */}
            {step === 2 && (
              <motion.div key="step2"
                initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.35, ease: EASE }}>
                <h2 className="font-display text-display-2 font-bold text-ink mb-2">{L.step2_title}</h2>
                <p className="text-ink/55 mb-8">{L.step2_sub}</p>
                <div className="grid grid-cols-2 gap-3">
                  {PRIORITIES.map((p) => {
                    const selected = selectedPriorities.includes(p.id);
                    const Icon = p.icon;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => togglePriority(p.id)}
                        className={`flex items-center gap-3 rounded-2xl border-2 p-4 transition-all duration-200 text-left
                          ${selected
                            ? "border-brand-pink bg-brand-pink/5"
                            : "border-mist bg-white hover:border-brand-pink/40"}`}
                      >
                        <span className={`h-9 w-9 rounded-xl flex items-center justify-center flex-shrink-0 ${selected ? "bg-brand-pink text-white" : "bg-mist text-ink/50"}`}>
                          <Icon className="h-4 w-4" />
                        </span>
                        <span className="font-semibold text-sm text-ink">{L.priority[p.id]}</span>
                        {selected && <Check className="h-4 w-4 text-brand-pink ml-auto" />}
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* STEP 3: Package results */}
            {step === 3 && (
              <motion.div key="step3"
                initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.35, ease: EASE }}>
                <h2 className="font-display text-display-2 font-bold text-ink mb-2">{L.step3_title}</h2>
                <p className="text-ink/55 mb-8">{L.step3_sub}</p>

                <div className="space-y-4">
                  {selectedServices.map((id) => {
                    const svc = SERVICES_DATA.find((s) => s.id === id);
                    const Icon = svc.icon;
                    const currentTier = selectedTiers[id] || "pro";

                    return (
                      <div key={id} className="rounded-2xl border border-mist bg-white p-5">
                        <div className="flex items-center gap-3 mb-4">
                          <span className={`h-9 w-9 rounded-xl bg-gradient-to-br ${svc.color} flex items-center justify-center text-white`}>
                            <Icon className="h-4 w-4" />
                          </span>
                          <div className="font-display font-bold text-ink">{L.service[id]}</div>
                        </div>
                              {/* Tier selector — responsive mobile layout */}
                        <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
                          {TIER_ORDER.map((tier) => {
                            const pricing = getPricing(id);
                            const price = pricing[tier];
                            const isSelected = currentTier === tier;
                            const isRecommended = tier === "pro";
                            return (
                              <button
                                key={tier}
                                type="button"
                                onClick={() => setSelectedTiers((prev) => ({ ...prev, [id]: tier }))}
                                className={`relative rounded-xl border-2 px-1 py-2.5 text-center transition-all duration-200
                                  ${isSelected ? "border-brand-pink bg-brand-pink/5" : "border-mist hover:border-brand-pink/30"}`}
                              >
                                {isRecommended && (
                                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 rounded-full bg-brand-pink text-white text-[0.5rem] px-1.5 py-0.5 font-bold whitespace-nowrap leading-tight">
                                    {L.recommended}
                                  </span>
                                )}
                                <div className="text-[0.6rem] sm:text-xs font-bold text-ink/70 capitalize leading-tight">{L.tier[tier]}</div>
                                <div className="mt-0.5 font-display text-xs sm:text-sm font-black text-ink">
                                  {price ? formatPrice(price) : "—"}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                        {/* Features for selected tier */}
                        {(() => {
                          const feats = getTierFeatures(id, currentTier);
                          return feats.length > 0 ? (
                            <ul className="mt-3 space-y-1.5">
                              {feats.map((feat, fi) => (
                                <li key={fi} className="flex items-start gap-2 text-xs text-ink/65">
                                  <Check className="h-3.5 w-3.5 text-brand-pink flex-shrink-0 mt-0.5" strokeWidth={3} />
                                  {feat}
                                </li>
                              ))}
                            </ul>
                          ) : null;
                        })()}
                      </div>
                    );
                  })}
                </div>

                {/* Total */}
                <div className="mt-6 rounded-2xl bg-gradient-to-br from-brand-pink to-brand-magenta text-cream p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-cream/70">{L.total}</p>
                      <p className="mt-1 font-display text-display-2 font-black leading-none">
                        {formatPrice(totalPrice)}
                        <span className="text-base font-normal text-cream/70 ml-1">{L.month}</span>
                      </p>
                    </div>
                    <div className="text-right text-sm text-cream/70">
                      {selectedServices.length} {lang === "tr" ? "hizmet" : "service"}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={sendWhatsApp}
                    className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-brand-pink-deep px-6 py-3.5 text-sm font-bold hover:scale-[1.02] transition-transform"
                  >
                    <MessageCircle className="h-4 w-4" />
                    {lang === "tr" ? "Bu Paketi İstiyorum — Hızlı İletişim" : "I Want This Package — Contact Us"}
                    <ChevronRight className="h-4 w-4" />
                  </button>
                  <p className="mt-2 text-center text-xs text-cream/60">
                    {lang === "tr" ? "veya " : "or "}
                    <a href={`/${lang}/contact`}
                      className="underline text-cream/80 hover:text-cream transition-colors">
                      {lang === "tr" ? "iletişim formu doldurun" : "fill the contact form"}
                    </a>
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Error */}
          {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

          {/* Navigation */}
          <div className="mt-8 flex items-center justify-between">
            {step > 1 ? (
              <button type="button" onClick={() => { setStep((s) => s - 1); setError(""); }}
                className="inline-flex items-center gap-2 text-sm text-ink/60 hover:text-ink transition-colors">
                <ArrowLeft className="h-4 w-4" /> {L.back}
              </button>
            ) : <div />}
            {step < 3 && (
              <button type="button" onClick={nextStep}
                className="inline-flex items-center gap-2 rounded-full bg-ink text-cream px-6 py-3 text-sm font-bold hover:bg-ink/80 transition-colors">
                {step === 2 ? L.show : L.next}
                <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Reviews */}
      {reviews.length > 0 && (
        <section className="bg-cream border-t border-mist py-section">
          <div className="container-page">
            <Reveal as="div" className="text-center mb-10">
              <span className="eyebrow justify-center">
                <Sparkles className="h-3 w-3 text-gold" />
                {L.reviews_title}
              </span>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {reviews.slice(0, 6).map((r, i) => (
                <Reveal key={r.id || i} as="article" delay={i * 0.06}
                  className="rounded-2xl border border-mist bg-white p-6 flex flex-col">
                  <div className="flex items-center gap-1 text-gold mb-4">
                    {Array.from({ length: r.rating || 5 }).map((_, k) => (
                      <Star key={k} className="h-4 w-4 fill-current" />
                    ))}
                  </div>
                  <blockquote className="font-display text-h3 leading-snug text-ink flex-1">
                    "{r.text}"
                  </blockquote>
                  <div className="mt-5 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-ink">{r.author}</p>
                      <p className="text-xs text-ink/50">{r.date}</p>
                    </div>
                    <span className="text-[0.65rem] font-bold uppercase tracking-[0.18em] text-ink/50 border border-mist rounded-full px-2.5 py-1">
                      {r.source || "Google"}
                    </span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
